import { Box, Confirm, Icon, Title, UserAvatar } from 'components'
import { useGetQuery, usePostQuery } from 'hooks'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSocket } from 'providers'
import {
  Container,
  Header,
  Controls,
  ControlButton,
  SongList,
  SongItem,
  SongDetails,
  SongTitle,
  DeleteButton,
  MoveButton,
  MoveControls,
} from './styles'

export const HostKaraokeManage = () => {
  const [queueItems, setQueueItems] = useState([])
  const [isPaused, setIsPaused] = useState(false)
  const socket = useSocket()
  const { slug } = useParams()

  const { data, error, isFetching, reFetch } = useGetQuery({
    url: `/host/queue/${slug}`
  })

  const { post: deleteSong } = usePostQuery({ method: 'DELETE', url: '' })
  const { post: reorderSongs } = usePostQuery({ url: '/host/queue_items/reorder' })
  const { post: updateQueue } = usePostQuery({ method: 'PUT', url: '' })

  const handleDeleteSong = (yes, id) => {
    if (!yes) return
    deleteSong({}, `/host/queue_items/${id}`)
    reFetch(true)
  }

  const moveItem = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= queueItems.length) return

    const newItems = [...queueItems]
    const [removed] = newItems.splice(index, 1)
    newItems.splice(newIndex, 0, removed)

    setQueueItems(newItems)
    reorderSongs({ items: newItems.map((item, i) => ({ id: item.id, position: i })) })
  }

  const handlePauseToggle = () => {
    const newPausedState = !isPaused
    setIsPaused(newPausedState)
    socket.emit(`/karaoke/${slug}/control`, { paused: newPausedState })
  }

  const handleRestart = () => {
    socket.emit(`/karaoke/${slug}/control`, { restart: true })
  }

  const handleSkip = () => {
    socket.emit(`/karaoke/${slug}/control`, { skip: true })
  }

  // Sync local state with server data
  useEffect(() => {
    if (data?.queue?.QueueItems) {
      setQueueItems(data.queue.QueueItems)
    }
    if (data?.queue?.state) {
      setIsPaused(data.queue.state === 'paused')
    }
  }, [data?.queue])

  useEffect(() => {
    socket.on(`/karaoke/${slug}`, (res) => {
      reFetch(true)
    })
  }, [socket, slug])

  return (
    <Container>
      <Header>
        <Title level="2">{data?.queue?.name || 'Karaoke Queue'}</Title>
        <Controls>
          <ControlButton onClick={handleRestart}>
            <Icon icon="restart" />
            Restart
          </ControlButton>
          <ControlButton onClick={handlePauseToggle} active={isPaused}>
            <Icon icon={isPaused ? 'play' : 'pause'} />
            {isPaused ? 'Resume' : 'Pause'}
          </ControlButton>
          <ControlButton onClick={handleSkip}>
            <Icon icon="skip-next" />
            Skip
          </ControlButton>
        </Controls>
      </Header>

      <SongList>
        {queueItems.map((item, index) => (
          <SongItem key={item.id}>
            <MoveControls>
              <MoveButton
                onClick={() => moveItem(index, -1)}
                disabled={index === 0}
                title="Move up"
              >
                <Icon icon="chevron-up" />
              </MoveButton>
              <MoveButton
                onClick={() => moveItem(index, 1)}
                disabled={index === queueItems.length - 1}
                title="Move down"
              >
                <Icon icon="chevron-down" />
              </MoveButton>
            </MoveControls>
            <UserAvatar user={item.details.user} />
            <SongDetails>
              <SongTitle>{item.details.user.full_name || item.details.user.email}</SongTitle>
              <SongTitle>{item.details.song.title}</SongTitle>
            </SongDetails>
            <Confirm
              handler={(yes) => handleDeleteSong(yes, item.id)}
              message={`Remove "${item.details.song.title}" from the queue?`}
            >
              <DeleteButton>
                <Icon icon="trash-can" />
              </DeleteButton>
            </Confirm>
          </SongItem>
        ))}
      </SongList>

      {queueItems.length === 0 && !isFetching && (
        <Box style={{ textAlign: 'center', padding: '2rem' }}>
          No songs in queue
        </Box>
      )}
    </Container>
  )
}
